import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar"; // Adjust the path as needed
import api from "../utils/api";
import adminPC from "../assets/adminPC.png";
import adminXB from "../assets/adminXB.png";
import adminPS from "../assets/adminPS.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import DatePicker from "react-datepicker";

const SellOrders = () => {
  const [sellOrders, setSellOrders] = useState([]);
  const [search, setSearch] = useState(""); // Search by Email
  const [dateRange, setDateRange] = useState([null, null]); // Start and End Date
  const [startDate, endDate] = dateRange; // Destructure dateRange
  const [filterPlatform, setFilterPlatform] = useState(""); // Filter by Platform
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const itemsPerPage = 10; // Orders per page

  useEffect(() => {
    const fetchSellOrders = async () => {
      try {
        const response = await api.get("/sell-coins/");
        setSellOrders(response.data.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch sell orders. Please try again.");
        setLoading(false);
      }
    };
    fetchSellOrders();
  }, []);

  // Filter orders based on search, date range, and platform
  const filteredOrders = sellOrders.filter((order) => {
    const matchesSearch = order.contactEmail
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesPlatform = !filterPlatform || order.platform === filterPlatform;

    const orderDate = new Date(order.createdAt);
    const matchesDateRange =
      (!startDate || orderDate >= startDate) &&
      (!endDate || orderDate <= endDate);

    return matchesSearch && matchesPlatform && matchesDateRange;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Get the orders for the current page
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetFilters = () => {
    setDateRange([null, null]);
    setFilterPlatform("");
    setSearch("");
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="flex flex-col md:flex-row">
      {/* Sidebar */}
      <Sidebar />

      {/* Content */}
      <div className="flex-1 bg-gray-50 p-4 md:p-6">
        <h1 className="text-lg md:text-2xl font-bold text-gray-700">Sell Orders</h1>

        {/* Search by Email */}
        <div className="flex flex-col md:flex-row items-start md:items-center mt-4 md:mt-6 mb-4 space-y-4 md:space-y-0 md:space-x-4">
          <input
            type="text"
            placeholder="Search by Email ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-1/3 focus:outline-none focus:ring focus:border-primary"
          />

          <button
            onClick={() => navigate(`/admin/setSellCoinsPrices`)}
            className="px-4 py-2 bg-primary text-black rounded-lg hover:bg-yellow-300 hover:text-black"
          >
            Set Sell Coins Price
          </button>

        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center space-y-4 md:space-y-0 md:space-x-4 mt-4 mb-4">
          {/* Date Range Picker */}
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable
            placeholderText="Select a date range"
            className="border rounded-lg px-4 py-2 w-full md:w-auto focus:outline-none focus:ring focus:border-primary"
          />

          {/* Filter by Platform */}
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-auto focus:outline-none focus:ring focus:border-primary"
          >
            <option value="">All Platforms</option>
            <option value="XB">Xbox</option>
            <option value="PS">PlayStation</option>
            <option value="PC">PC</option>
          </select>

          {/* Reset Filters */}
          <button
            onClick={resetFilters}
            className="text-red-500 hover:text-red-700 font-medium w-full md:w-auto"
          >
            Reset Filters
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <p className="text-gray-600">Loading orders...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <>
            {/* Desktop Table (hidden on mobile) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-700">Order#ID</th>
                    <th className="px-4 py-2 text-left text-gray-700">Contact Email</th>
                    <th className="px-4 py-2 text-left text-gray-700">Total Coins</th>
                    <th className="px-4 py-2 text-left text-gray-700">Platform</th>
                    <th className="px-4 py-2 text-left text-gray-700">PayPal Email</th>
                    <th className="px-4 py-2 text-left text-gray-700">Payment Method</th>
                    <th className="px-4 py-2 text-left text-gray-700">Order Paid</th>
                    <th className="px-4 py-2 text-left text-gray-700">View Details</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order, index) => (
                    <tr key={index} className="border-b">
                      <td className="px-4 py-2 text-gray-700">{order.orderId}</td>
                      <td className="px-4 py-2 text-gray-700">{order.contactEmail}</td>
                      <td className="px-4 py-2 text-gray-700">{order.totalCoins}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {order.platform === "XB" ? (
                          <img src={adminXB} alt="Xbox" className="w-6 h-6" />
                        ) : order.platform === "PS" ? (
                          <img src={adminPS} alt="PlayStation" className="w-6 h-6" />
                        ) : (
                          <img src={adminPC} alt="PC" className="w-6 h-6" />
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{order.paypalEmail}</td>
                      <td className="px-4 py-2 text-gray-700">{order.paymentMethod}</td>
                      <td className="px-4 py-2 text-gray-700">
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${order.orderPaid
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                            }`}
                        >
                          {order.orderPaid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => navigate(`/admin/sell-order-info/${order._id}`)}
                          className="px-4 py-2 bg-primary text-black rounded-lg hover:bg-yellow-300"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Layout (hidden on desktop) */}
            <div className="md:hidden space-y-4">
              {paginatedOrders.map((order, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-4">
                  <div className="space-y-2">
                    <p className="text-gray-700"><span className="font-semibold">Order#ID:</span> {order.orderId}</p>
                    <p className="text-gray-700"><span className="font-semibold">Contact Email:</span> {order.contactEmail}</p>
                    <p className="text-gray-700"><span className="font-semibold">Total Coins:</span> {order.totalCoins}</p>
                    <p className="text-gray-700"><span className="font-semibold">Platform:</span>
                      {order.platform === "XB" ? (
                        <img src={adminXB} alt="Xbox" className="w-6 h-6 inline-block ml-2" />
                      ) : order.platform === "PS" ? (
                        <img src={adminPS} alt="PlayStation" className="w-6 h-6 inline-block ml-2" />
                      ) : (
                        <img src={adminPC} alt="PC" className="w-6 h-6 inline-block ml-2" />
                      )}
                    </p>
                    <p className="text-gray-700"><span className="font-semibold">PayPal Email:</span> {order.paypalEmail}</p>
                    <p className="text-gray-700"><span className="font-semibold">Payment Method:</span> {order.paymentMethod}</p>
                    <p className="text-gray-700">
                      <span className="font-semibold">Order Paid:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-sm ml-2 ${order.orderPaid
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                          }`}
                      >
                        {order.orderPaid ? "Paid" : "Unpaid"}
                      </span>
                    </p>
                    <button
                      onClick={() => navigate(`/admin/sell-order-info/${order._id}`)}
                      className="w-full px-4 py-2 bg-primary text-black rounded-lg hover:bg-yellow-300"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination (responsive) */}
            <div className="flex items-center justify-center mt-4 space-x-4">
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default SellOrders;
